<div class="flex w-full flex-col gap-4" aria-live="polite">
  <p *ngIf="loadingExisting" class="text-sm text-gray-600" role="status">Checking for an existing application…</p>

  <ng-container *ngIf="submitted as application; else applicationForm">
    <div *ngIf="isProcessing" class="rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800" role="status">
      <p class="font-semibold">
        {{
          application.status === 'finalizing'
            ? 'Your financed warranty is being finalized.'
            : 'Your financing application is processing.'
        }}
      </p>
    </div>

    <div
      *ngIf="isSuccessful"
      class="rounded border border-green-200 bg-green-50 p-3 text-sm text-green-800"
      role="status"
    >
      <p class="font-semibold">
        {{ application.status === 'completed' ? 'Financing completed.' : 'Financing approved.' }}
      </p>
      <p *ngIf="applicationReference">Application reference: {{ applicationReference }}</p>
    </div>

    <div
      *ngIf="application.status === 'completion_error'"
      class="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
      role="status"
    >
      Lemur confirmed the financing, but WarrantySphere could not finish the warranty yet. Refresh to safely retry; do
      not submit another application.
    </div>

    <div *ngIf="isReady" class="rounded border border-green-200 bg-green-50 p-3 text-sm text-green-800" role="status">
      <p class="font-semibold">Your financing results are ready.</p>
      <p *ngIf="applicationReference">Application reference: {{ applicationReference }}</p>
      <p *ngIf="!application.approvedOffers?.length">No offers are currently available. You can refresh or pay now.</p>
    </div>

    <div
      *ngIf="application.status === 'declined'"
      class="rounded border border-gray-300 bg-gray-50 p-3 text-sm text-gray-800"
      role="status"
    >
      The financing application was declined. You can pay now or start a deliberate new application.
    </div>

    <div
      *ngIf="
        application.status === 'expired' || application.status === 'cancelled' || application.status === 'abandoned'
      "
      class="rounded border border-gray-300 bg-gray-50 p-3 text-sm text-gray-800"
      role="status"
    >
      The financing application was {{ application.status }}. You can pay now or start a new application.
    </div>

    <div
      *ngIf="
        application.status === 'error' || application.status === 'provider_error' || application.status === 'conflict'
      "
      class="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800"
      role="alert"
    >
      {{ errorMessage }}
    </div>

    <div
      *ngIf="application.status === 'polling_exhausted'"
      class="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
      role="status"
    >
      Automatic status checks were interrupted. Refresh to retrieve the latest result; do not resubmit applicant data.
    </div>

    <div *ngIf="application.approvedOffers?.length" class="flex flex-col gap-3" aria-labelledby="lemur-offers-title">
      <h3 id="lemur-offers-title" class="font-semibold">Available financing offers</h3>
      <div *ngFor="let offer of application.approvedOffers" class="rounded border p-3 text-sm">
        <p class="font-medium">{{ offer.lender.name }}</p>
        <p><span class="font-medium">Amount:</span> {{ moneyRange(offer.amount) }}</p>
        <p *ngIf="offer.apr"><span class="font-medium">APR:</span> {{ percentRange(offer.apr) }}</p>
        <p *ngIf="offer.term"><span class="font-medium">Term:</span> {{ monthRange(offer.term) }}</p>
        <p *ngIf="offer.monthly_payment">
          <span class="font-medium">Monthly payment:</span> {{ moneyRange(offer.monthly_payment) }}
        </p>
        <p *ngIf="offer.lender.disclaimer" class="mt-2 text-xs text-gray-600">{{ offer.lender.disclaimer }}</p>
        <a class="mt-3 inline-block underline" [href]="offer.continue_url" target="_blank" rel="noopener noreferrer">
          Continue with {{ offer.lender.name }}
        </a>
      </div>
    </div>

    <div class="flex flex-col gap-2 sm:flex-row">
      <button
        *ngIf="
          isProcessing ||
          isReady ||
          application.status === 'completed' ||
          application.status === 'polling_exhausted' ||
          application.status === 'completion_error'
        "
        type="button"
        vs-button
        theme="primary"
        [disabled]="refreshing"
        (click)="refreshStatus()"
      >
        {{ refreshing ? 'Refreshing…' : 'Refresh status' }}
      </button>
      <button *ngIf="canRetrySameApplication" type="button" vs-button theme="primary" (click)="retrySameApplication()">
        Retry this application
      </button>
      <button
        *ngIf="canStartNewApplication"
        type="button"
        vs-button
        appearance="outline"
        (click)="beginNewApplication()"
      >
        Start a new application
      </button>
      <button *ngIf="canReturnToPayNow" type="button" vs-button appearance="outline" (click)="choosePayNow()">
        Return to pay now
      </button>
    </div>
  </ng-container>

  <ng-template #applicationForm>
    <div *ngIf="errorKind" class="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800" role="alert">
      {{ errorMessage }}
    </div>

    <div
      *ngIf="!loadingExisting && (!disclosureVersion || !disclosureUrl)"
      class="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
      role="status"
    >
      Lemur financing is not fully configured. Return to pay now or contact support.
    </div>

    <form
      *ngIf="!loadingExisting && disclosureVersion && disclosureUrl"
      class="grid w-full gap-3"
      (ngSubmit)="submit()"
    >
      <div class="grid w-full gap-3 sm:grid-cols-2">
        <vs-form-field [config]="{ label: 'Date of birth *' }">
          <input vsInput type="date" autocomplete="off" [formControl]="form.controls.dateOfBirth" />
          <span
            *ngIf="form.controls.dateOfBirth.touched && form.controls.dateOfBirth.invalid"
            class="text-danger"
            slot="hint"
          >
            Enter a valid date of birth.
          </span>
        </vs-form-field>

        <vs-form-field [config]="{ label: 'Government ID number *' }">
          <input
            vsInput
            type="text"
            inputmode="numeric"
            autocomplete="off"
            placeholder="123-45-6789"
            [formControl]="form.controls.governmentIdNumber"
          />
          <span
            *ngIf="form.controls.governmentIdNumber.touched && form.controls.governmentIdNumber.invalid"
            class="text-danger"
            slot="hint"
          >
            Enter a 9-digit government ID / SSN.
          </span>
        </vs-form-field>

        <vs-form-field [config]="{ label: 'Annual income (USD) *' }">
          <input vsInput type="number" min="0" step="0.01" [formControl]="form.controls.annualIncome" />
        </vs-form-field>

        <vs-form-field [config]="{ label: 'Housing status *' }">
          <select
            class="w-full h-10 rounded-md border border-gray-300 px-3 text-sm"
            [formControl]="form.controls.housingStatus"
          >
            <option value="">Select housing status</option>
            <option *ngFor="let status of housingStatuses" [value]="status">{{ statusLabel(status) }}</option>
          </select>
        </vs-form-field>

        <vs-form-field [config]="{ label: 'Employment status *' }">
          <select
            class="w-full h-10 rounded-md border border-gray-300 px-3 text-sm"
            [formControl]="form.controls.employmentStatus"
          >
            <option value="">Select employment status</option>
            <option *ngFor="let status of employmentStatuses" [value]="status">{{ statusLabel(status) }}</option>
          </select>
        </vs-form-field>
      </div>

      <div class="flex flex-col gap-2 rounded bg-gray-50 border p-3 text-sm text-gray-700">
        <p class="font-medium">Required disclosures</p>
        <p>
          Review the shared Lemur financing application disclosure, including the disclosures of Lemur's financing
          partners.
        </p>
        <a class="font-medium underline" [href]="disclosureUrl" target="_blank" rel="noopener noreferrer">
          Open financing application disclosure
        </a>
        <p class="text-xs text-gray-500">Disclosure version: {{ disclosureVersion }}</p>
        <label class="flex items-start gap-2">
          <input type="checkbox" [formControl]="form.controls.accepted" />
          <span>{{ checkoutConfig.LEMUR_CONSENT_LABEL }} *</span>
        </label>
      </div>

      <div class="flex flex-col gap-2 sm:flex-row">
        <button
          type="submit"
          vs-button
          theme="primary"
          [disabled]="form.invalid || submitting || !sessionId || !disclosureVersion"
        >
          {{ submitting ? 'Submitting…' : checkoutConfig.LEMUR_SUBMIT_LABEL }}
        </button>
        <button type="button" vs-button appearance="outline" [disabled]="submitting" (click)="choosePayNow()">
          Return to pay now
        </button>
      </div>
    </form>
  </ng-template>
</div>
